import { useCallback, useEffect, useMemo, useState } from 'react';

import { getPhaseViews, setStorageValue } from 'App/Helpers';

import { canShowPwaPhase } from './helpers';

/**
 * Adds InstallPhase gating (show/views/total) on top of install-prompt state.
 * Mounted only when `PwaProvider` is used with `enablePhase`.
 *
 * Passes the merged context value to `children` so the provider stays in
 * `PwaProvider`.
 *
 * @param {Object} props
 * @param {string} props.storageKey
 * @param {{
 *   pwaStatus: string,
 *   deferredPwaPrompt: *,
 *   pwaPromptAttempt: number,
 *   showInstallPrompt: Function,
 * }} props.install
 * @param {(contextValue: Object) => import('react').ReactNode} props.children
 */
const PwaPhaseGate = ({ storageKey, install, children }) => {
    const [showPwaPhase, setShowPwaPhase] = useState(() =>
        canShowPwaPhase(storageKey)
    );
    const [pwaPhaseViews, setPwaPhaseViews] = useState(() =>
        getPhaseViews(storageKey, 'pwa.phaseViews')
    );
    const [pwaPhaseTotal, setPwaPhaseTotal] = useState(undefined);

    useEffect(() => {
        if (pwaPhaseTotal === undefined) return;

        const curShowPwaPhase =
            install.pwaStatus === 'NotInstalled' &&
            pwaPhaseTotal > pwaPhaseViews;

        setShowPwaPhase(curShowPwaPhase);
        setStorageValue(storageKey, 'pwa.showPhase', curShowPwaPhase);
    }, [install.pwaStatus, pwaPhaseTotal, pwaPhaseViews, storageKey]);

    const updatePwaPhaseViews = useCallback(() => {
        setPwaPhaseViews((prevValue) => {
            const pwaViews = prevValue + 1;

            setStorageValue(storageKey, 'pwa.phaseViews', pwaViews);

            return pwaViews;
        });
    }, [storageKey]);

    const contextValue = useMemo(
        () => ({
            ...install,
            showPwaPhase,
            setPwaPhaseTotal,
            updatePwaPhaseViews,
        }),
        [install, showPwaPhase, updatePwaPhaseViews]
    );

    return children(contextValue);
};

export { PwaPhaseGate };
